import { api, extractListItems, extractPaging, extractResponsePaging, normalizePaging, type ApiResponse } from '../client';

export interface Employee {
  uuid: string;
  email: string;
  email_verified_at: string | null;
  full_name: string;
  phone: string;
  credit_total: number;
  user_login_method: any;
  user_role: {
    uuid: string;
    name: string;
  };
  user_status: {
    uuid: string;
    name: string;
  };
  job_title: string;
  location: string;
  department: string;
  total_donated_hours: number;
  total_donation_amount: number;
  total_pending_hours: number;
  last_donation_request_date: string | null;
  donation_requests: any;
  organization: any;
  employers: EmployerInfo[];
}

export interface EmployerInfo {
  employer_name: string;
  total_donation_amount: number;
  total_hours: number;
  total_pending_hours: number;
}

export interface AdminEmployeesFilters {
  page?: number;
  per_page?: number;
  phrase?: string;
  employer_uuid?: string;
  user_status_uuid?: string;
  start_date?: string;
  end_date?: string;
}

export interface AdminEmployeesResponse {
  success: boolean;
  message?: string;
  data: Employee[];
  paging: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
  };
  errors?: string[];
}

class AdminEmployeesService {
  private baseUrl = '/admin/employees';

  // Get paginated list of employees with filters
  async getEmployees(filters: AdminEmployeesFilters = {}): Promise<AdminEmployeesResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.per_page) params.append('per_page', filters.per_page.toString());
      if (filters.phrase) params.append('phrase', filters.phrase);
      if (filters.employer_uuid) params.append('employer_uuid', filters.employer_uuid);
      if (filters.user_status_uuid) params.append('user_status_uuid', filters.user_status_uuid);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await api.get<AdminEmployeesResponse>(`${this.baseUrl}?${params.toString()}`);
      const items = extractListItems<Employee>(response.data.data);
      const paging = normalizePaging(
        extractResponsePaging(response) ?? extractPaging(response.data.data),
        items.length
      );
      return {
        success: true,
        data: items,
        paging: paging as AdminEmployeesResponse['paging'],
        message: 'Employees retrieved successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        data: [],
        paging: {
          current_page: 0,
          last_page: 0,
          per_page: 15,
          total: 0,
          from: 0,
          to: 0
        },
        message: error.response?.data?.message || 'Failed to retrieve employees',
        errors: [error.message]
      };
    }
  }

  // Get employee by UUID
  async getEmployee(uuid: string): Promise<{ success: boolean; data: Employee | null; message: string }> {
    try {
      const response = await api.get<{ data: Employee }>(`${this.baseUrl}/${uuid}`);
      
      return {
        success: true,
        data: response.data.data,
        message: 'Employee retrieved successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || 'Failed to retrieve employee'
      };
    }
  }

}

export const adminEmployeesService = new AdminEmployeesService();
export default adminEmployeesService; 